import React, { useState, useEffect, useRef } from 'react';
import { useReactions } from '../hooks/useReactions';
import { useStreamAuth } from '../hooks/useStreamAuth';

/**
 * Netflix-style expandable reaction buttons component
 * Shows single thumbs up icon that expands to show like/double-like/dislike options
 */
const ReactionButtons = ({ 
  channelId, 
  initialReaction = null,
  size = 'medium',
  className = '',
  onReactionChange = null 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { isAuthenticated } = useStreamAuth();
  const componentRef = useRef(null);
  
  // Use the custom reactions hook
  const { 
    currentReaction, 
    isLoading, 
    error, 
    setReaction, 
    hasReaction
  } = useReactions(channelId, initialReaction);

  // Handle clicks outside component to collapse on touch devices
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (componentRef.current && !componentRef.current.contains(event.target) && isExpanded) {
        setIsExpanded(false);
        setIsHovered(false);
      }
    };

    // Add event listeners for both mouse and touch
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isExpanded]);

  // Debug logging for search issues
  console.log('🔍 ReactionButtons render:', {
    isAuthenticated,
    channelId,
    currentReaction,
    isLoading,
    error,
    isExpanded
  });

  // Don't render anything if user is not authenticated or no channelId
  if (!isAuthenticated || !channelId) {
    console.log('🔍 ReactionButtons: Not rendering - auth:', isAuthenticated, 'channelId:', channelId);
    return null;
  }

  /**
   * Handle reaction button click
   * @param {string} action - The reaction type: 'like', 'dislike', 'double_like'
   */
  const handleReaction = async (action) => {
    console.log('🔍 ReactionButtons: handleReaction called:', { action, isLoading, channelId });
    
    if (isLoading) {
      console.log('🔍 ReactionButtons: Blocked - already loading');
      return;
    }

    console.log('🔍 ReactionButtons: Calling setReaction...');
    const success = await setReaction(action);
    console.log('🔍 ReactionButtons: setReaction result:', success);
    
    if (success) {
      // Notify parent component of reaction change
      if (onReactionChange) {
        const newReaction = currentReaction === action ? null : action;
        onReactionChange(newReaction);
      }

      // Collapse after selection
      setIsExpanded(false);
      setIsHovered(false);
    }
  };

  /**
   * Handle mouse enter - expand the component
   */
  const handleMouseEnter = () => {
    if (!isLoading) {
      setIsHovered(true);
      setIsExpanded(true);
    }
  };

  /**
   * Handle mouse leave - collapse the component
   */
  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsExpanded(false);
  };

  /**
   * Handle tap/click on collapsed button - expand for touch devices
   */
  const handleCollapsedClick = (e) => {
    console.log('🔍 ReactionButtons: handleCollapsedClick called:', { isLoading, isExpanded });
    
    e.preventDefault();
    e.stopPropagation();
    
    if (isLoading) {
      console.log('🔍 ReactionButtons: Blocked - loading');
      return;
    }

    // If already expanded, don't do anything (let individual buttons handle clicks)
    if (isExpanded) {
      console.log('🔍 ReactionButtons: Already expanded, ignoring click');
      return;
    }

    console.log('🔍 ReactionButtons: Expanding component...');
    // Expand the component for touch devices
    setIsExpanded(true);
    setIsHovered(true);

    // Auto-collapse after 5 seconds if no interaction
    setTimeout(() => {
      if (!isLoading) {
        setIsExpanded(false);
        setIsHovered(false);
      }
    }, 5000);
  };

  /**
   * Handle touch start - same as click but for touch devices
   */
  const handleCollapsedTouch = (e) => {
    // Prevent mouse events from firing after touch
    e.preventDefault();
    handleCollapsedClick(e);
  };

  /**
   * Handle clicks outside the component to collapse on touch devices
   */
  const handleOutsideClick = () => {
    if (isExpanded) {
      setIsExpanded(false);
      setIsHovered(false);
    }
  };

  /**
   * Get the icon to show when collapsed based on current reaction
   */
  const getCollapsedIcon = () => {
    if (currentReaction === 'dislike') {
      return <ThumbsDownIcon filled={true} />;
    } else if (currentReaction === 'double_like') {
      return <ThumbsUpIcon filled={true} enhanced={true} />;
    } else if (currentReaction === 'like') {
      return <ThumbsUpIcon filled={true} />;
    } else {
      // Default state - show empty thumbs up
      return <ThumbsUpIcon filled={false} />;
    }
  };

  /**
   * Get the title for the collapsed state
   */
  const getCollapsedTitle = () => {
    if (currentReaction === 'dislike') {
      return "Rated: Thumbs Down";
    } else if (currentReaction === 'double_like') {
      return "Rated: Love This";
    } else if (currentReaction === 'like') {
      return "Rated: Thumbs Up";
    } else {
      return "Rate this content";
    }
  };

  return (
    <div 
      ref={componentRef}
      className={`reaction-buttons reaction-buttons--${size} ${isExpanded ? 'reaction-buttons--expanded' : 'reaction-buttons--collapsed'} ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Error Display */}
      {error && (
        <div className="reaction-error">
          Failed to update reaction
        </div>
      )}
      {/* Collapsed State - Single Icon */}
      {!isExpanded && (
        <button
          className={`reaction-button reaction-button--collapsed ${currentReaction ? 'reaction-button--active' : ''}`}
          onClick={handleCollapsedClick}
          onTouchStart={handleCollapsedTouch}
          disabled={isLoading}
          title={getCollapsedTitle()}
          aria-label={getCollapsedTitle()}
        >
          {isLoading ? (
            <div className="reaction-loading-spinner" />
          ) : (
            getCollapsedIcon()
          )}
        </button>
      )}

      {/* Expanded State - All Three Options */}
      {isExpanded && (
        <div className="reaction-buttons-expanded">
          {/* Like Button */}
          <button
            className={`reaction-button reaction-button--like ${currentReaction === 'like' ? 'reaction-button--active' : ''}`}
            onClick={() => handleReaction('like')}
            disabled={isLoading}
            title={currentReaction === 'like' ? 'Remove like' : 'Like this content'}
            aria-label={currentReaction === 'like' ? 'Remove like' : 'Like this content'}
          >
            <ThumbsUpIcon filled={currentReaction === 'like'} />
          </button>

          {/* Double Like Button */}
          <button
            className={`reaction-button reaction-button--double-like ${currentReaction === 'double_like' ? 'reaction-button--active' : ''}`}
            onClick={() => handleReaction('double_like')}
            disabled={isLoading}
            title={currentReaction === 'double_like' ? 'Remove love' : 'Love this content'}
            aria-label={currentReaction === 'double_like' ? 'Remove love' : 'Love this content'}
          >
            <ThumbsUpIcon filled={currentReaction === 'double_like'} enhanced={true} />
          </button>

          {/* Dislike Button */}
          <button
            className={`reaction-button reaction-button--dislike ${currentReaction === 'dislike' ? 'reaction-button--active' : ''}`}
            onClick={() => handleReaction('dislike')}
            disabled={isLoading}
            title={currentReaction === 'dislike' ? 'Remove dislike' : 'Dislike this content'}
            aria-label={currentReaction === 'dislike' ? 'Remove dislike' : 'Dislike this content'}
          >
            <ThumbsDownIcon filled={currentReaction === 'dislike'} />
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Thumbs Up Icon Component
 */
const ThumbsUpIcon = ({ filled = false, enhanced = false }) => (
  <svg 
    width="16" 
    height="16" 
    viewBox="0 0 24 24" 
    fill={filled ? "currentColor" : "none"} 
    stroke="currentColor" 
    strokeWidth="2"
    className={`reaction-icon ${enhanced ? 'reaction-icon--enhanced' : ''}`}
  >
    <path d="M7 10v12"/>
    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/>
    {enhanced && (
      <>
        <circle cx="18" cy="6" r="2" fill="currentColor" opacity="0.8"/>
        <path d="M16 4l2 2 2-2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      </>
    )}
  </svg>
);

/**
 * Thumbs Down Icon Component
 */
const ThumbsDownIcon = ({ filled = false }) => (
  <svg 
    width="16" 
    height="16" 
    viewBox="0 0 24 24" 
    fill={filled ? "currentColor" : "none"} 
    stroke="currentColor" 
    strokeWidth="2"
    className="reaction-icon"
  >
    <path d="M17 14V2"/>
    <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"/>
  </svg>
);

export default ReactionButtons;
